import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Copy, CheckCircle2, Mail } from "lucide-react";

export default function AddCoachDialog({ open, onOpenChange, onCreated, defaultRole = "coach", lockRole = false }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    weekly_hours_target: 20,
    role: defaultRole,
    meet_link: "",
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // {user, invite_link, invite_expires_at}
  const [copied, setCopied] = useState(false);

  // Keep form.role in sync when defaultRole changes (e.g. user switches between Coaches and Dozenten pages)
  React.useEffect(() => {
    setForm((f) => ({ ...f, role: defaultRole }));
  }, [defaultRole, open]);

  const reset = () => {
    setForm({ name: "", email: "", weekly_hours_target: 20, role: defaultRole, meet_link: "" });
    setResult(null);
    setCopied(false);
  };

  const submit = async () => {
    if (!form.name || !form.email) {
      toast.error("Bitte Name und E-Mail angeben");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/coaches", {
        name: form.name,
        email: form.email,
        weekly_hours_target: parseInt(form.weekly_hours_target, 10) || 20,
        role: form.role,
        meet_link: form.meet_link.trim() || null,
      });
      toast.success(`${data.user.name} eingeladen`);
      onCreated?.(data.user);
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    if (!result?.invite_link) return;
    try {
      await navigator.clipboard.writeText(result.invite_link);
      setCopied(true);
      toast.success("Einladungslink kopiert");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-lg rounded-none border border-slate-200 p-0" data-testid="add-coach-dialog">
        <DialogHeader className="px-8 pt-8 pb-4 border-b border-slate-100">
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">
            {result ? "Einladung erstellt" : "Coach einladen"}
          </div>
          <DialogTitle className="font-heading text-3xl font-light tracking-tighter text-slate-900 mt-1">
            {result ? "Link verschicken" : "Neuer Coach"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Lade einen neuen Coach per E-Mail-basiertem Einladungslink ein.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <>
            <div className="px-8 py-6 space-y-5">
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Name</label>
                <input
                  data-testid="add-coach-name"
                  type="text"
                  className="input-tech"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">E-Mail</label>
                <input
                  data-testid="add-coach-email"
                  type="email"
                  className="input-tech font-mono"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
                <p className="text-xs text-slate-500 mt-2">
                  Die eingeladene Person muss diese E-Mail später zum Anmelden verwenden.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">
                    Wochenstunden-Ziel
                  </label>
                  <input
                    data-testid="add-coach-target"
                    type="number"
                    min="0"
                    className="input-tech font-mono"
                    value={form.weekly_hours_target}
                    onChange={(e) => setForm({ ...form, weekly_hours_target: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Rolle</label>
                  <select
                    data-testid="add-coach-role"
                    className="input-tech disabled:bg-slate-100 disabled:cursor-not-allowed"
                    value={form.role}
                    disabled={lockRole}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                  >
                    <option value="coach">Coach — AVGS-Kundenbetreuung</option>
                    <option value="lecturer">Dozent — Weiterbildungsklassen</option>
                    <option value="manager">Manager — darf Kunden anlegen und bearbeiten (nicht löschen)</option>
                    <option value="admin">Admin — voller Zugriff (Kunden und Coaches anlegen/löschen)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">
                  Persönlicher Meet-Link (optional)
                </label>
                <input
                  data-testid="add-coach-meet-link"
                  type="url"
                  placeholder="https://meet.google.com/xxx-yyyy-zzz  oder  calendar.app.google/…"
                  className="input-tech font-mono text-sm"
                  value={form.meet_link}
                  onChange={(e) => setForm({ ...form, meet_link: e.target.value })}
                />
                <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                  Fester Google-Meet- oder Buchungslink dieses Coaches — wird bei Online-Terminen automatisch in die Einladungs-Mail eingebettet.
                </p>
              </div>
            </div>
            <DialogFooter className="px-8 py-5 border-t border-slate-100 flex gap-3">
              <button
                data-testid="add-coach-cancel"
                onClick={() => onOpenChange(false)}
                className="px-6 py-3 text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Abbrechen
              </button>
              <button
                data-testid="add-coach-save"
                onClick={submit}
                disabled={saving}
                className="px-6 py-3 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {saving ? "Wird gesendet…" : "Einladung erstellen"}
              </button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="px-8 py-6 space-y-5">
              <div className="flex items-start gap-3 border-l-2 border-emerald-600 pl-4 py-1">
                <CheckCircle2 size={18} strokeWidth={1.5} className="text-emerald-600 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-slate-900">
                    {result.user.name} wurde eingeladen
                  </div>
                  <div className="text-xs text-slate-500 font-mono mt-1">{result.user.email}</div>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">
                  <Mail size={11} strokeWidth={1.5} className="inline mr-1" />
                  Einladungslink (gültig 14 Tage)
                </label>
                <div className="border border-slate-200 bg-slate-50 p-3 text-xs font-mono text-slate-700 break-all" data-testid="invite-link-output">
                  {result.invite_link}
                </div>
                <button
                  data-testid="invite-copy-button"
                  onClick={copyLink}
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 border border-slate-900 bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 transition-colors"
                >
                  {copied ? <CheckCircle2 size={12} strokeWidth={2} /> : <Copy size={12} strokeWidth={1.5} />}
                  {copied ? "Kopiert!" : "Link kopieren"}
                </button>
              </div>

              <p className="text-xs text-slate-500 leading-relaxed">
                Schicke diesen Link an <span className="font-mono">{result.user.email}</span> — die Person legt
                selbst ein Passwort fest und wird damit automatisch angemeldet.
              </p>
            </div>
            <DialogFooter className="px-8 py-5 border-t border-slate-100">
              <button
                data-testid="invite-done-button"
                onClick={handleClose}
                className="px-6 py-3 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 transition-colors"
              >
                Fertig
              </button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
